/* Importing the necessary libraries and files */
import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Form, FormikProvider, useFormik } from 'formik';

/* Importing the components from the material-ui library. */
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Close as CloseIcon, Person as PersonIcon, VideoCall as VideoCallIcon } from '@mui/icons-material';

/* Importing the functions from the Redux slices. */
import { searchDevice } from '../../features/device/deviceSlice';
import { searchEncounter } from '../../features/encounter/encounterSlice';
import { getLocation } from '../../features/location/locationSlice';
import { getPatientAll } from '../../features/patient/patientSlice';

/* Importing components. */
import AutoCompletePatient from '../../components/AutoCompletePatient';

/* Importing the functions from the utils folder. */
import { fEncounter } from '../../utils/Encounter/Encounter.utils.format';

const IconWrapperStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}));

/**
 * Renders the CallPatient component.
 * This component allows the user to call a patient and select a device for the call.
 * It includes form validation and search functionality for patients and devices.
 */
export default function CallPatient() {
  const colorPatient = 'primary';

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { Locations } = useSelector((state) => state.location);
  const { searchResults: deviceSearchResults } = useSelector((state) => state.device);
  const { searchResults: ecounterSearchResults } = useSelector((state) => state.encounter);

  const [hasNearDevice, setHasNearDevice] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: {
      subject: '',
    },
    onSubmit: (values) => {
      if (values.subject) {
        navigate(`/meeting/call/device-${values.subject}`, { replace: false, state: { new: true } });
      } else {
        const randomRoomName = Math.floor(Math.random() * 1000000).toString();
        navigate(`/meeting/call/room-${randomRoomName}`, { replace: false, state: { new: true } });
      }
    },
  });

  const { errors, touched, handleSubmit, setFieldValue } = formik;

  const searchDeviceByLocation = useCallback(async () => {
    if (!selectedLocation) return;
    setIsSearching(true);
    await Promise.all([
      dispatch(getLocation(selectedLocation)),
      dispatch(searchDevice({ query: `location=Location/${selectedLocation}` })),
    ]);
  }, [dispatch, selectedLocation]);

  const findNearestDevice = useCallback(() => {
    if (!selectedLocation) return;
    if (deviceSearchResults.length > 0) {
      setDeviceOptions(deviceSearchResults.map((d) => ({ name: d.resource.deviceName[0]?.name, id: d.resource.id })));
      setSelectedDevice(deviceSearchResults[0].resource.id);
      setHasNearDevice(true);
      setIsSearching(false);
      return;
    }
    const location = Locations.find((l) => l.id === selectedLocation);
    if (location?.partOf?.reference) {
      setSelectedLocation(location.partOf.reference.split('/')[1]);
    } else {
      setHasNearDevice(false);
      setIsSearching(false);
    }
  }, [Locations, deviceSearchResults, selectedLocation]);

  const findLatestEncounter = useCallback(() => {
    if (!selectedPatient) return;
    const EncountersFiltered = ecounterSearchResults.filter(
      (p) => p.resource.subject?.reference === `Patient/${selectedPatient}`
    );
    if (EncountersFiltered.length > 0) {
      const encounter = fEncounter(EncountersFiltered[0].resource);
      if (encounter.location) {
        setSelectedLocation(encounter.location);
      }
    } else {
      setHasNearDevice(false);
      setIsSearching(false);
    }
  }, [ecounterSearchResults, selectedPatient]);

  const searchPatientEncounter = useCallback(async () => {
    if (!selectedPatient) return;
    setIsSearching(true);
    await dispatch(searchEncounter({ query: `patient=Patient/${selectedPatient}&_sort=-date&_count=1` }));
  }, [dispatch, selectedPatient]);

  useEffect(() => {
    searchPatientEncounter();
  }, [searchPatientEncounter]);

  useEffect(() => {
    findLatestEncounter();
  }, [findLatestEncounter]);

  useEffect(() => {
    findNearestDevice();
  }, [findNearestDevice]);

  useEffect(() => {
    searchDeviceByLocation();
  }, [searchDeviceByLocation]);

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
    dispatch(getPatientAll());
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPatient(null);
  };

  return (
    <>
      <Card>
        <CardActionArea
          sx={{
            py: 5,
            boxShadow: 3,
            textAlign: 'center',
          }}
          onClick={handleClickOpen}
        >
          <IconWrapperStyle
            sx={{
              color: (theme) => theme.palette[colorPatient].dark,
              backgroundImage: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette[colorPatient].dark, 0)} 0%, ${alpha(
                  theme.palette[colorPatient].dark,
                  0.24
                )} 100%)`,
            }}
          >
            <PersonIcon />
          </IconWrapperStyle>
          <Typography variant="h6">Call a Patient</Typography>
        </CardActionArea>
      </Card>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Call a Patient</Typography>
                <IconButton onClick={handleClose} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                <AutoCompletePatient
                  setFieldValue={(field, value) => setSelectedPatient(value)}
                  fieldName={'patient'}
                  values={{ patient: selectedPatient }}
                  label="Select a Patient"
                />

                {isSearching && (
                  <>
                    <Typography variant="body1">Searching for nearest device...</Typography>
                    <LinearProgress />
                  </>
                )}
                {!isSearching && !hasNearDevice && selectedPatient && (
                  <>
                    <Typography variant="body2">No nearby device found for this patient.</Typography>
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body1">Create a meeting room instead?</Typography>
                      <Button variant="contained" type="submit" startIcon={<VideoCallIcon />} sx={{ width: '250px' }}>
                        New meeting
                      </Button>
                    </Box>
                  </>
                )}
                {!isSearching && hasNearDevice && selectedPatient && (
                  <>
                    <Autocomplete
                      fullWidth
                      autoHighlight
                      options={deviceOptions}
                      getOptionLabel={(option) => deviceOptions.find((el) => el.id === option)?.name || ''}
                      isOptionEqualToValue={(option, value) => option.id === value}
                      value={selectedDevice}
                      onChange={(e, value) => {
                        setSelectedDevice(value?.id);
                        setFieldValue('subject', value?.id);
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Available Devices"
                          error={Boolean(touched.subject && errors.subject)}
                          helperText={touched.subject && errors.subject}
                        />
                      )}
                    />
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button variant="contained" type="submit" startIcon={<VideoCallIcon />} sx={{ width: '250px' }}>
                        Call device
                      </Button>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
          </Form>
        </FormikProvider>
      </Dialog>
    </>
  );
}
